import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GenericInfoModal from '../../../../components/GenericInfoModal';

interface UnsavedChangesValue {
  /** A form says whether it holds edits that are not saved. */
  setDirty: (dirty: boolean) => void;
  /** Runs `proceed` now, or after the admin agrees to drop the edits. */
  guard: (proceed: () => void) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesValue>({ setDirty: () => undefined, guard: proceed => proceed() });

export const useUnsavedChanges = () => useContext(UnsavedChangesContext);

/**
 * Keeps unsaved edits from being lost by leaving the page.
 *
 * The app runs on BrowserRouter, where react-router's useBlocker is not available, so
 * this covers the ways off the page it can see: closing or reloading the tab
 * (beforeunload), and the section tabs and kindergarten switch on this page, which ask
 * through `guard`. A sidebar link is not covered.
 */
export const UnsavedChangesProvider: React.FC<{ children: (guard: (proceed: () => void) => void) => React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const dirtyRef = useRef(false);
  const [pending, setPending] = useState<(() => void) | null>(null);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      event.preventDefault();
      // still needed by some browsers to show their prompt at all
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  const setDirty = useCallback((dirty: boolean) => {
    dirtyRef.current = dirty;
  }, []);

  const guard = useCallback((proceed: () => void) => {
    if (dirtyRef.current) setPending(() => proceed);
    else proceed();
  }, []);

  return (
    <UnsavedChangesContext.Provider value={{ setDirty, guard }}>
      {children(guard)}
      <GenericInfoModal
        open={!!pending}
        onClose={() => setPending(null)}
        onConfirm={() => {
          const proceed = pending;
          dirtyRef.current = false;
          setPending(null);
          proceed?.();
        }}
        label={t('billing.settings.unsaved.leaveTitle')}
        message={t('billing.settings.unsaved.leaveMessage')}
        confirmLabel={t('billing.settings.unsaved.leaveConfirm')}
        closeLabel={t('billing.settings.unsaved.stay')}
      />
    </UnsavedChangesContext.Provider>
  );
};
